import { Target, Trophy, Calendar, Briefcase, Zap, Coins, CheckCircle, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getUserQuests, getAvailableQuests } from '../../lib/gamification/xpEngine';
import { useAuthStore } from '../../store/authStore';

interface Quest {
  id: string;
  title: string;
  description: string;
  quest_type: 'daily' | 'weekly' | 'achievement' | 'onboarding';
  target_count: number;
  xp_reward: number;
  credit_reward: number;
  icon: string;
  active: boolean;
}

interface UserQuest {
  id: string;
  quest_id: string;
  progress: number;
  completed: boolean;
  completed_at: string | null;
  started_at: string;
  quest: Quest;
}

const QUEST_TYPE_COLORS = {
  daily: 'from-blue-500 to-cyan-500',
  weekly: 'from-purple-500 to-pink-500',
  achievement: 'from-amber-500 to-orange-500',
  onboarding: 'from-green-500 to-emerald-500',
};

const QUEST_TYPE_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  achievement: 'Achievement',
  onboarding: 'Getting Started',
};

export function QuestDashboard() {
  const { user } = useAuthStore();
  const [userQuests, setUserQuests] = useState<UserQuest[]>([]);
  const [availableQuests, setAvailableQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'available'>('active');

  useEffect(() => {
    if (user) {
      loadQuests();
    }
  }, [user]);

  const loadQuests = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [userQuestsData, availableQuestsData] = await Promise.all([
        getUserQuests(user.id),
        getAvailableQuests(),
      ]);
      setUserQuests(userQuestsData);
      setAvailableQuests(availableQuestsData);
    } catch (error) {
      console.error('Error loading quests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Target className="w-7 h-7 text-purple-600" />
          Quests
        </h2>

        {/* Tab Switcher */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
              activeTab === 'active'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Active ({userQuests.length})
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
              activeTab === 'available'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Available
          </button>
        </div>
      </div>

      {/* Quest List */}
      {activeTab === 'active' ? (
        <div className="space-y-4">
          {userQuests.length === 0 ? (
            <div className="text-center py-12 px-4 bg-gray-50 rounded-xl">
              <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium text-lg mb-2">No active quests</p>
              <p className="text-gray-500">Check out available quests to get started!</p>
              <button
                onClick={() => setActiveTab('available')}
                className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                View Available Quests
              </button>
            </div>
          ) : (
            userQuests.map((userQuest) => (
              <QuestCard key={userQuest.id} userQuest={userQuest} onUpdate={loadQuests} />
            ))
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableQuests.map((quest) => (
            <AvailableQuestCard key={quest.id} quest={quest} />
          ))}
        </div>
      )}
    </div>
  );
}

interface QuestCardProps {
  userQuest: UserQuest;
  onUpdate: () => void;
}

function QuestCard({ userQuest }: QuestCardProps) {
  const { quest, progress, completed } = userQuest;
  const progressPercent = Math.min(100, (progress / quest.target_count) * 100);

  return (
    <div className={`rounded-xl border-2 p-6 transition-all ${
      completed
        ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50'
        : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-lg'
    }`}>
      <div className="flex items-start gap-4">
        {/* Quest Icon */}
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${QUEST_TYPE_COLORS[quest.quest_type]} flex items-center justify-center flex-shrink-0`}>
          {quest.icon === 'calendar' && <Calendar className="w-7 h-7 text-white" />}
          {quest.icon === 'briefcase' && <Briefcase className="w-7 h-7 text-white" />}
          {quest.icon === 'file-text' && <Target className="w-7 h-7 text-white" />}
          {quest.icon === 'users' && <Trophy className="w-7 h-7 text-white" />}
        </div>

        {/* Quest Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-2 bg-gradient-to-r ${QUEST_TYPE_COLORS[quest.quest_type]} text-white`}>
                {QUEST_TYPE_LABELS[quest.quest_type]}
              </span>
              <h3 className="text-lg font-bold text-gray-900">{quest.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{quest.description}</p>
            </div>

            {completed && (
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            )}
          </div>

          {/* Progress Bar */}
          {!completed && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600 font-medium">
                  {progress} / {quest.target_count}
                </span>
                <span className="text-purple-600 font-bold">{progressPercent.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${QUEST_TYPE_COLORS[quest.quest_type]} transition-all duration-300`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Rewards */}
          <div className="flex items-center gap-4 text-sm">
            {quest.xp_reward > 0 && (
              <div className="flex items-center gap-1 text-yellow-600 font-semibold">
                <Zap className="w-4 h-4" />
                <span>+{quest.xp_reward} XP</span>
              </div>
            )}
            {quest.credit_reward > 0 && (
              <div className="flex items-center gap-1 text-purple-600 font-semibold">
                <Coins className="w-4 h-4" />
                <span>+{quest.credit_reward} Credits</span>
              </div>
            )}
            {quest.quest_type === 'daily' && !completed && (
              <div className="flex items-center gap-1 text-gray-500 ml-auto">
                <Clock className="w-4 h-4" />
                <span>Resets daily</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AvailableQuestCardProps {
  quest: Quest;
}

function AvailableQuestCard({ quest }: AvailableQuestCardProps) {
  return (
    <div className="rounded-xl border-2 border-gray-200 bg-white p-6 hover:border-purple-300 hover:shadow-lg transition-all">
      <div className="flex items-start gap-4">
        {/* Quest Icon */}
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${QUEST_TYPE_COLORS[quest.quest_type]} flex items-center justify-center flex-shrink-0`}>
          {quest.icon === 'calendar' && <Calendar className="w-6 h-6 text-white" />}
          {quest.icon === 'briefcase' && <Briefcase className="w-6 h-6 text-white" />}
          {quest.icon === 'file-text' && <Target className="w-6 h-6 text-white" />}
          {quest.icon === 'users' && <Trophy className="w-6 h-6 text-white" />}
        </div>

        {/* Quest Info */}
        <div className="flex-1 min-w-0">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-2 bg-gradient-to-r ${QUEST_TYPE_COLORS[quest.quest_type]} text-white`}>
            {QUEST_TYPE_LABELS[quest.quest_type]}
          </span>
          <h3 className="text-base font-bold text-gray-900 mb-1">{quest.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{quest.description}</p>

          {/* Rewards */}
          <div className="flex items-center gap-3 text-sm">
            {quest.xp_reward > 0 && (
              <div className="flex items-center gap-1 text-yellow-600 font-semibold">
                <Zap className="w-4 h-4" />
                <span>+{quest.xp_reward} XP</span>
              </div>
            )}
            {quest.credit_reward > 0 && (
              <div className="flex items-center gap-1 text-purple-600 font-semibold">
                <Coins className="w-4 h-4" />
                <span>+{quest.credit_reward}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
